import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Plus } from "lucide-react";
import { useState } from "react";
import { PersonaPicker } from "@/components/persona-picker";
import {
      DashboardPageActions,
      DashboardPageHeader,
      DashboardPageShell,
} from "@/components/patterns/headers";
import { SearchesTable } from "@/components/search-specifications-table/search-specifications-table";
import {
      DEFAULT_SEARCH_SORT_STATE,
      type SearchSortState,
} from "@/components/search-specifications-table/utils";
import { Button } from "@/components/ui/button";
import {
      Dialog,
      DialogContent,
      DialogDescription,
      DialogFooter,
      DialogHeader,
      DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { trpc } from "@/lib/trpc";
import { useBrandStore } from "@/state/store/brand";

export const Route = createFileRoute("/_dashboard/$brand/searches/")({
      component: RouteComponent,
});

function RouteComponent() {
      const selectedBrandId = useBrandStore((state) => state.selectedBrandId);

      const queryClient = useQueryClient();
      const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
      const [newSearchName, setNewSearchName] = useState("New Search");
      const [newSearchPersonaId, setNewSearchPersonaId] = useState<string | null>(null);
      const [sorting, setSorting] = useState<SearchSortState>(DEFAULT_SEARCH_SORT_STATE);

      const createSearchMutation = useMutation(
            trpc.searchSpecifications.createSearchSpecification.mutationOptions({
                  onSuccess: () => {
                        queryClient.invalidateQueries({
                              queryKey: trpc.searchSpecifications.getBrandSearchSpecifications.queryKey({
                                    brandId: selectedBrandId!,
                              }),
                        });
                  },
            }),
      );

      const { data: brand } = useQuery(
            trpc.brands.getBrand.queryOptions({ id: selectedBrandId! }, { enabled: !!selectedBrandId }),
      );
      const navigate = useNavigate();

      const handleCreateSearch = async (e: React.FormEvent) => {
            e.preventDefault();
            if (!newSearchName.trim()) return;

            const newSearch = await createSearchMutation.mutateAsync({
                  brandId: selectedBrandId!,
                  name: newSearchName.trim(),
                  description: "",
                  llmIds: [],
                  personaId: newSearchPersonaId,
            });

            setIsCreateDialogOpen(false);
            setNewSearchName("New Search");
            setNewSearchPersonaId(null);

            navigate({
                  to: "/$brand/searches/$search",
                  params: { brand: brand!.slug, search: newSearch.id },
            });
      };

      const handleOpenCreateDialog = () => {
            setNewSearchName("New Search");
            setNewSearchPersonaId(null);
            setIsCreateDialogOpen(true);
      };

      return (
            <DashboardPageShell as="div" contentClassName="space-y-6">
                  <DashboardPageHeader
                        title="Searches"
                        description="Manage your search specifications and executions"
                        aside={
                              <DashboardPageActions>
                                    <Button onClick={handleOpenCreateDialog} className="gap-2">
                                          <Plus className="h-4 w-4" />
                                          Create New Search
                                    </Button>
                              </DashboardPageActions>
                        }
                  />
                  <SearchesTable
                        brandId={selectedBrandId}
                        sorting={sorting}
                        onSortingChange={setSorting}
                  />

                  <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
                        <DialogContent className="sm:max-w-xl overflow-x-hidden">
                              <DialogHeader>
                                    <DialogTitle>Create New Search</DialogTitle>
                                    <DialogDescription>
                                          Give your search specification a name and optionally assign a target persona.
                                    </DialogDescription>
                              </DialogHeader>
                              <form onSubmit={handleCreateSearch}>
                                    <div className="space-y-4 py-4">
                                          <div className="space-y-2">
                                                <Label htmlFor="search-name">Name</Label>
                                                <Input
                                                      id="search-name"
                                                      value={newSearchName}
                                                      onChange={(e) => setNewSearchName(e.target.value)}
                                                      placeholder="e.g. Organic Discovery"
                                                      variant="brand"
                                                      autoFocus
                                                />
                                          </div>
                                          <div className="space-y-2">
                                                <Label>Persona</Label>
                                                <PersonaPicker
                                                      brandId={selectedBrandId!}
                                                      value={newSearchPersonaId}
                                                      onChange={setNewSearchPersonaId}
                                                />
                                                <p className="text-xs text-muted-foreground">
                                                      Personas make AI-generated prompts more targeted to your audience.
                                                </p>
                                          </div>
                                    </div>
                                    <DialogFooter>
                                          <Button
                                                type="button"
                                                variant="outline"
                                                className="bg-card"
                                                onClick={() => setIsCreateDialogOpen(false)}
                                          >
                                                Cancel
                                          </Button>
                                          <Button
                                                type="submit"
                                                disabled={!newSearchName.trim() || createSearchMutation.isPending}
                                          >
                                                {createSearchMutation.isPending ? "Creating" : "Create Search"}
                                          </Button>
                                    </DialogFooter>
                              </form>
                        </DialogContent>
                  </Dialog>
            </DashboardPageShell>
      );
}
